import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { fetchAnalytics, fetchAdmins, createAdmin, deleteAdmin, fetchAuditLogs, fetchSystemBackup } from './index';

interface VisitorData {
  date: string;
  count: number;
}

interface AdminUser {
  _id: string;
  name: string;
  email: string;
  phone: string;
}

interface AuditLog {
  _id: string;
  action: string;
  details: string;
  performedBy: string;
  timestamp: string;
}

const SuperAdminDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [activeTab, setActiveTab] = useState<'overview' | 'admins' | 'logs'>('overview');
  const [visitors, setVisitors] = useState<VisitorData[]>([]);
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Form State for New Admin
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '', phone: '', address: '' });

  useEffect(() => {
    const loadData = async () => {
      try {
        const analyticsRes = await fetchAnalytics();
        setVisitors(analyticsRes.data);
        // We can lazy load other data when tabs switch, but for simplicity loading here
        // or we can load them in separate useEffects based on activeTab
      } catch (error) {
        console.error('Failed to load analytics', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === 'admins') {
      fetchAdmins().then(res => setAdmins(res.data)).catch(console.error);
    } else if (activeTab === 'logs') {
      fetchAuditLogs().then(res => setLogs(res.data)).catch(console.error);
    }
  }, [activeTab]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createAdmin(newAdmin);
      alert('Admin created successfully');
      setShowAddAdmin(false);
      setNewAdmin({ name: '', email: '', password: '', phone: '', address: '' });
      // Refresh list
      const res = await fetchAdmins();
      setAdmins(res.data);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create admin');
    }
  };

  const handleDeleteAdmin = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      try {
        await deleteAdmin(id);
        setAdmins(admins.filter(a => a._id !== id));
      } catch (error) {
        console.error(error);
        alert('Failed to delete admin');
      }
    }
  };

  const handleBackup = async () => {
    try {
      const { data } = await fetchSystemBackup();
      const jsonString = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `kano_fresh_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error(error);
      alert('Backup failed');
    }
  };

  return (
    <div className="min-h-screen bg-secondary">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-serif font-bold text-primary">Super Admin Dashboard</h1>
          <div className="flex gap-4">
            <button 
              onClick={() => navigate('/admin')}
              className="bg-white text-primary border border-primary px-4 py-2 rounded hover:bg-gray-50"
            >
              Go to Admin View
            </button>
            <button 
              onClick={handleLogout}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-gray-200 mb-8">
          <button 
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-4 font-medium text-sm ${activeTab === 'overview' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Overview
          </button>
          <button 
            onClick={() => setActiveTab('admins')}
            className={`py-2 px-4 font-medium text-sm ${activeTab === 'admins' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Manage Administrators
          </button>
          <button 
            onClick={() => setActiveTab('logs')}
            className={`py-2 px-4 font-medium text-sm ${activeTab === 'logs' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Audit Logs
          </button>
        </div>

        {/* Content Area */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Visitor Analytics Card */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
              <h2 className="text-xl font-serif font-bold text-gray-800 mb-4">Website Traffic (Last 7 Days)</h2>
              
              {loading ? (
                <p className="text-gray-500">Loading stats...</p>
              ) : (
                <div className="space-y-4">
                  {visitors.length === 0 ? (
                    <p className="text-gray-500">No data available yet.</p>
                  ) : (
                    visitors.map((v, index) => (
                      <div key={index} className="flex justify-between items-center border-b border-gray-50 pb-2">
                        <span className="text-gray-600 font-sans">{v.date}</span>
                        <span className="font-bold text-primary text-lg">{v.count} Visitors</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Quick Actions Card */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
              <h2 className="text-xl font-serif font-bold text-gray-800 mb-4">System Management</h2>
              <div className="space-y-3">
                <button onClick={() => setActiveTab('admins')} className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded text-gray-700 font-medium">Manage Administrators</button>
                <button onClick={() => setActiveTab('logs')} className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded text-gray-700 font-medium">View Audit Logs</button>
                <button onClick={handleBackup} className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded text-gray-700 font-medium">Database Backup (JSON)</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'admins' && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-serif font-bold text-gray-800">Administrators</h2>
              <button onClick={() => setShowAddAdmin(!showAddAdmin)} className="bg-primary text-white px-4 py-2 rounded text-sm hover:bg-green-800">
                {showAddAdmin ? 'Cancel' : '+ Add New Admin'}
              </button>
            </div>

            {showAddAdmin && (
              <form onSubmit={handleCreateAdmin} className="mb-8 bg-gray-50 p-4 rounded border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <input type="text" placeholder="Full Name" required className="p-2 border rounded" value={newAdmin.name} onChange={e => setNewAdmin({...newAdmin, name: e.target.value})} />
                  <input type="email" placeholder="Email" required className="p-2 border rounded" value={newAdmin.email} onChange={e => setNewAdmin({...newAdmin, email: e.target.value})} />
                  <input type="password" placeholder="Password" required className="p-2 border rounded" value={newAdmin.password} onChange={e => setNewAdmin({...newAdmin, password: e.target.value})} />
                  <input type="text" placeholder="Phone" required className="p-2 border rounded" value={newAdmin.phone} onChange={e => setNewAdmin({...newAdmin, phone: e.target.value})} />
                  <input type="text" placeholder="Address" required className="p-2 border rounded md:col-span-2" value={newAdmin.address} onChange={e => setNewAdmin({...newAdmin, address: e.target.value})} />
                </div>
                <button type="submit" className="bg-accent text-white px-4 py-2 rounded text-sm hover:bg-yellow-600">Create Admin</button>
              </form>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {admins.map((admin) => (
                    <tr key={admin._id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{admin.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{admin.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{admin.phone}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button onClick={() => handleDeleteAdmin(admin._id)} className="text-red-600 hover:text-red-900">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
            <h2 className="text-xl font-serif font-bold text-gray-800 mb-6">System Audit Logs</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performed By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log) => (
                    <tr key={log._id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-700">{log.action}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{log.details}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.performedBy}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(log.timestamp).toLocaleString()}</td>
                    </tr>
                  ))}
                  {logs.length === 0 && (
                    <tr><td colSpan={4} className="px-6 py-4 text-center text-gray-500">No logs found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;